import {_} from 'lodash';
import {IRule, IRuleset} from "../../../index";
import PromiseRequest from "../../../req/PromiseRequest";
import {createErrorString} from "../../../util/misc";

const SUB_RULESET_OPERATORS: Array<IRule["argumentoperator"]> = ["Omfattes av", "Omfattes ikke av"];

function pickRulesetWithRules(candidate: IRuleset, known?: IRuleset): IRuleset {
    if (!known) {
        return candidate;
    }
    return Array.isArray(known.rules) || !Array.isArray(candidate.rules) ? known : candidate;
}

export function collectUniqueRulesetsFromRoots(
    rootRulesets: IRuleset[],
    rulesetsById: Map<number, IRuleset>,
): IRuleset[] {
    const seen = new Set<number>();
    const rulesetsSeenById = new Map<number, IRuleset>();
    const stack = [...rootRulesets];
    while (stack.length) {
        const candidate = stack.pop();
        if (!candidate || seen.has(candidate.id)) {
            continue;
        }
        const rs = pickRulesetWithRules(candidate, rulesetsById.get(candidate.id));
        seen.add(rs.id);
        rulesetsSeenById.set(rs.id, rs);
        for (const rule of rs.rules || []) {
            if (!SUB_RULESET_OPERATORS.includes(rule.argumentoperator)) {
                continue;
            }
            const refId = parseInt(String(rule.argument), 10);
            if (Number.isNaN(refId)) {
                continue;
            }
            const child = rulesetsById.get(refId);
            if (child) {
                stack.push(child);
            }
        }
    }
    return Array.from(seen)
        .map((id) => pickRulesetWithRules(rulesetsSeenById.get(id)!, rulesetsById.get(id)))
        .filter(Boolean) as IRuleset[];
}

export function topologicalCopyOrder(unique: IRuleset[]): IRuleset[] {
    const idSet = new Set(unique.map((r) => r.id));
    const prereqs = new Map<number, Set<number>>();
    for (const r of unique) {
        const ps = new Set<number>();
        for (const rule of r.rules || []) {
            if (!SUB_RULESET_OPERATORS.includes(rule.argumentoperator)) {
                continue;
            }
            const refId = parseInt(String(rule.argument), 10);
            if (!Number.isNaN(refId) && idSet.has(refId)) {
                ps.add(refId);
            }
        }
        prereqs.set(r.id, ps);
    }
    const remaining = new Set(unique.map((r) => r.id));
    const byId = _.keyBy(unique, "id") as Record<number, IRuleset>;
    const result: IRuleset[] = [];
    while (remaining.size) {
        const readyId = Array.from(remaining).find((id) => {
            const deps = prereqs.get(id) || new Set();
            return Array.from(deps).every((d) => !remaining.has(d));
        });
        if (readyId === undefined) {
            Array.from(remaining)
                .sort((a, b) => a - b)
                .forEach((id) => {
                    const item = byId[id];
                    if (item) {
                        result.push(item);
                    }
                    remaining.delete(id);
                });
            break;
        }
        remaining.delete(readyId);
        const ready = byId[readyId];
        if (ready) {
            result.push(ready);
        }
    }
    return result;
}

function buildRulesetShellForCreate(src: IRuleset, trgOrgnr: string, name: string): IRuleset {
    const cpy = _.cloneDeep(src);
    cpy.id = -1;
    cpy.companyid = trgOrgnr;
    cpy.name = name;
    cpy.rules = [];
    cpy.refs = null;
    return cpy;
}

function cloneRuleForCreate(rule: IRule, newRulesetId: number, sourceIdToTargetId: Map<number, number>): IRule {
    const r = _.cloneDeep(rule);
    r.id = 0;
    r.rulesetid = newRulesetId;
    if (SUB_RULESET_OPERATORS.includes(r.argumentoperator)) {
        const refId = parseInt(String(r.argument), 10);
        if (!Number.isNaN(refId)) {
            const mapped = sourceIdToTargetId.get(refId);
            if (mapped !== undefined) {
                r.argument = mapped;
            }
        }
    }
    return r;
}

export type ICopyRulesetResult =
    | {sourceId: number; success: true; targetRuleset: IRuleset}
    | {sourceId: number; success: false; error: string};

export function copyRulesetsWithApis(
    orderedUnique: IRuleset[],
    trgOrgnr: string,
    nameForSourceId: Map<number, string>,
): Promise<ICopyRulesetResult[]> {
    const sourceIdToTargetId = new Map<number, number>();
    const results: ICopyRulesetResult[] = [];

    const runOne = (src: IRuleset): Promise<void> => {
        const chosenName = nameForSourceId.get(src.id) ?? src.name;
        const shell = buildRulesetShellForCreate(src, trgOrgnr, chosenName);
        let created: IRuleset | undefined;
        return new PromiseRequest("createruleset")
            .request({
                jruleset: JSON.stringify(shell),
                orgnr: trgOrgnr,
            })
            .then((data: IRuleset[]) => {
                created = data[0];
                if (!created) {
                    return;
                }
                sourceIdToTargetId.set(src.id, created.id);
                const rules = _.sortBy(src.rules || [], "order");
                return rules.reduce(
                    (chain: Promise<void>, rule: IRule) =>
                        chain.then(() => {
                            if (!created) {
                                return Promise.resolve();
                            }
                            const jrule = cloneRuleForCreate(rule, created.id, sourceIdToTargetId);
                            return new PromiseRequest("createrule").request({
                                rulesetid: created.id,
                                jrule: JSON.stringify(jrule),
                                orgnr: trgOrgnr,
                            });
                        }),
                    Promise.resolve(),
                );
            })
            .then(() => {
                results.push({sourceId: src.id, success: true, targetRuleset: created!});
            })
            .catch((reason: string) => {
                if (created) {
                    sourceIdToTargetId.delete(src.id);
                }
                results.push({
                    sourceId: src.id,
                    success: false,
                    error: createErrorString(reason, "_blank"),
                });
            });
    };

    return orderedUnique.reduce(
        (p, src) => p.then(() => runOne(src)),
        Promise.resolve() as Promise<void>,
    ).then(() => results);
}
